import { Info, Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import { StatCard, StatCardRow } from '@/modules/common/components/molecules/stat-card';
import { formatDisplayDate } from '@/utils/date-formatters';

import { getTimeOffTypeIcon } from '../../constants/time-off-type-icon-map';
import { buildBalanceInfoTooltipItems } from '../../helpers/balance-info-tooltip.helper';
import { TimeOffBalance } from '../../types/time-off-balance';
import { BalanceAvailableValue } from './balance-available-value';

interface TimeOffBalanceCardProps {
  balance: TimeOffBalance;
  /** When provided, renders a pencil that opens the adjust-balance flow. */
  onEdit?: () => void;
}

export const TimeOffBalanceCard = ({ balance, onEdit }: TimeOffBalanceCardProps) => {
  const { t } = useTranslation('authenticated.timeOff');
  const Icon = getTimeOffTypeIcon(balance.type.code);

  const editAction = onEdit ? (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground"
      aria-label={t('member.adjust.editAriaLabel', { type: balance.type.displayName })}
      onClick={onEdit}
    >
      <Pencil className="h-4 w-4" />
    </Button>
  ) : undefined;

  const formatDaysValue = (value: number) => t('balances.daysValue', { count: value });

  const infoTooltipContent = (
    <ul className="m-0 list-disc space-y-1 pl-4 text-xs">
      {buildBalanceInfoTooltipItems(balance.info).map(item => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );

  // The "Total booked for period" tooltip explains the period: effective date → today.
  const bookedTooltipContent = balance.info.effectiveDate ? (
    <p className="text-xs">
      {t('balances.bookedTooltip.description', {
        from: formatDisplayDate(balance.info.effectiveDate),
        to: formatDisplayDate(new Date()),
      })}
    </p>
  ) : undefined;

  return (
    <StatCard
      icon={<Icon className="h-4 w-4" />}
      title={balance.type.displayName}
      infoTooltip={infoTooltipContent}
      headerAction={editAction}
    >
      <StatCardRow
        label={t('balances.rows.balance')}
        value={formatDaysValue(balance.balanceDays)}
      />
      <StatCardRow
        label={t('balances.rows.available')}
        value={
          balance.requestedDays != null ? (
            <BalanceAvailableValue
              primary={formatDaysValue(balance.availableDays)}
              secondary={formatDaysValue(balance.requestedDays)}
              secondaryTooltip={
                <div className="space-y-1">
                  <p className="font-semibold">
                    {t('balances.requestedTooltip.title', { count: balance.requestedDays })}
                  </p>
                  <p className="text-xs">{t('balances.requestedTooltip.description')}</p>
                </div>
              }
            />
          ) : (
            formatDaysValue(balance.availableDays)
          )
        }
      />
      <StatCardRow
        label={
          <span className="flex items-center gap-1">
            {t('balances.rows.totalBooked')}
            {bookedTooltipContent && (
              <SimpleTooltip content={bookedTooltipContent}>
                <button
                  type="button"
                  className="text-muted-foreground transition-colors hover:text-foreground"
                  aria-label={t('balances.bookedTooltip.ariaLabel')}
                >
                  <Info className="h-3.5 w-3.5" />
                </button>
              </SimpleTooltip>
            )}
          </span>
        }
        value={formatDaysValue(balance.bookedDays)}
      />
    </StatCard>
  );
};
